import { useEffect, useState } from "react";
import { X, Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * PageIntroCard — a friendly, dismissible one-liner shown at the top of a
 * newly-restructured page. Once dismissed, it never shows again for that
 * user on that page (localStorage keyed by user_id + pageKey).
 *
 * Props:
 *   pageKey   — unique string per page (e.g. "study-hub", "enrollments-tabs")
 *   title     — bold intro line
 *   body      — friendly explanation of what changed / how the page works
 *   icon      — optional lucide icon component (defaults to Info)
 *   tone      — "indigo" (default) | "amber" | "emerald"
 */
export default function PageIntroCard({ pageKey, title, body, icon: Icon = Info, tone = "indigo" }) {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(true); // start hidden — flip on mount if not previously dismissed

  const storageKey = user?.id ? `pm_intro_v1_${pageKey}_${user.id}` : null;

  useEffect(() => {
    if (!storageKey) return;
    try {
      const prev = localStorage.getItem(storageKey);
      setDismissed(!!prev);
    } catch {
      setDismissed(false);
    }
  }, [storageKey]);

  const close = () => {
    setDismissed(true);
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, String(Date.now())); } catch { /* noop */ }
  };

  if (dismissed) return null;

  const tones = {
    indigo: {
      wrap: "bg-gradient-to-br from-indigo-50 to-blue-50 border-indigo-200 dark:from-indigo-950/40 dark:to-blue-950/40 dark:border-indigo-800",
      iconBg: "bg-indigo-600 text-white",
      title: "text-indigo-900 dark:text-indigo-100",
    },
    amber: {
      wrap: "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 dark:from-amber-950/40 dark:to-orange-950/40 dark:border-amber-800",
      iconBg: "bg-amber-600 text-white",
      title: "text-amber-900 dark:text-amber-100",
    },
    emerald: {
      wrap: "bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200 dark:from-emerald-950/40 dark:to-teal-950/40 dark:border-emerald-800",
      iconBg: "bg-emerald-600 text-white",
      title: "text-emerald-900 dark:text-emerald-100",
    },
  };
  const t = tones[tone] || tones.indigo;

  return (
    <div
      className={`relative rounded-xl border px-4 py-3 sm:px-5 sm:py-4 flex items-start gap-3 ${t.wrap}`}
      data-testid={`page-intro-${pageKey}`}
      role="note"
    >
      <div className={`h-9 w-9 shrink-0 rounded-lg grid place-items-center ${t.iconBg} shadow-sm`}>
        <Icon className="w-4.5 h-4.5" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0 pr-6">
        <div className={`text-sm font-bold leading-tight ${t.title}`}>{title}</div>
        {body && (
          <div className="text-xs sm:text-sm text-slate-700 dark:text-slate-200 mt-1 leading-relaxed">
            {body}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={close}
        aria-label="Dismiss intro"
        className="absolute top-2 right-2 h-7 w-7 rounded-lg grid place-items-center text-slate-500 hover:text-slate-800 hover:bg-white/70 dark:hover:bg-slate-800/60 transition-colors"
        data-testid={`page-intro-${pageKey}-dismiss`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
